
import React from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import { DateRange } from 'react-day-picker';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface FilterDateProps {
  dateRange: {
    startDate: string;
    endDate: string;
  };
  onDateChange: (dateRange: { startDate: string; endDate: string }) => void;
  defaultMonth?: Date; // New prop to control the calendar's default view
}

const FilterDate = ({ dateRange, onDateChange, defaultMonth }: FilterDateProps) => {
  // Convert string dates to Date objects for the Calendar component
  const [date, setDate] = React.useState<DateRange | undefined>(() => {
    if (dateRange.startDate || dateRange.endDate) {
      return {
        from: dateRange.startDate ? new Date(dateRange.startDate) : undefined,
        to: dateRange.endDate ? new Date(dateRange.endDate) : undefined,
      };
    }
    return undefined;
  });
  
  // State to control the popover being open or closed
  const [isOpen, setIsOpen] = React.useState(false);

  // Update date range when the calendar selection changes
  const handleSelect = (selectedRange: DateRange | undefined) => {
    setDate(selectedRange);
    
    // Format dates as ISO strings with time set to beginning of day (00:00:00)
    const formatDate = (date: Date | undefined) => {
      if (!date) return '';
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d.toISOString();
    };
    
    onDateChange({
      startDate: selectedRange?.from ? formatDate(selectedRange.from) : '',
      endDate: selectedRange?.to ? formatDate(selectedRange.to) : '',
    });
    
    // If both from and to dates are selected, close the popover
    if (selectedRange?.from && selectedRange?.to) {
      setIsOpen(false);
    }
  };

  // Function to clear the date range
  const handleClear = () => {
    setDate(undefined);
    onDateChange({
      startDate: '',
      endDate: '',
    });
    setIsOpen(false);
  };

  // Jump to today
  const handleJumpToToday = () => {
    const today = new Date();
    setDate({
      from: today,
      to: undefined
    });
    onDateChange({
      startDate: today.toISOString(),
      endDate: ''
    });
  };

  return (
    <div>
      <div className="flex items-center mb-2 text-sm font-medium text-gray-600">
        <CalendarIcon size={16} className="mr-1 text-medihub-primary" />
        <span>Dátum</span>
      </div>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "yyyy. MM. dd.", { locale: hu })} - {format(date.to, "yyyy. MM. dd.", { locale: hu })}
                </>
              ) : (
                format(date.from, "yyyy. MM. dd.", { locale: hu })
              )
            ) : (
              <span>Válassz dátumtartományt</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="p-2 md:p-3">
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={defaultMonth || date?.from || new Date()}
              selected={date}
              onSelect={handleSelect}
              numberOfMonths={1}
              locale={hu}
              className={cn("pointer-events-auto")}
              classNames={{
                cell: "text-center h-8 w-8 p-0 focus-within:relative focus-within:z-20",
                day: "h-8 w-8 p-0 aria-selected:opacity-100",
              }}
            />
            <div className="flex justify-between p-2 border-t mt-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleJumpToToday}
                className="text-xs"
              >
                Mai nap
              </Button>
              {date && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleClear}
                  className="text-xs"
                >
                  Törlés
                </Button>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default FilterDate;
